import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import {  studentCourses as fetchStudentCoursesAPI, newFetchTeacherByCourseId, createPackage, walletPoints, getCoursePrice, savePackage, triggerExternalPayment, getAccountDetails, createSplitPackage, updateSplitPackage, getSplitPreview } from '../../../../../Api/apiUtils';
import { ConfigContext, PaymentConfigContext } from '../../../../../Api/global/ApiContext';
import { Input } from "reactstrap";
import { Button, Field, TextInput, Select, Table, DescriptionList } from "../../../../../ui";
import { Callout, Dialog, Actions, FormGrid } from "../ui-pending";
import { createCustomPlan, calculetePackage, createExternalCustomPlan, createExternalPaymentRequest } from '../../../../../utils/paymentUtils';
import { toast } from "react-toastify";
import "./style.scss";
import "./custom-payment.scss";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MixpanelEvents,trackEvent } from '../../../../../utils/mixpanelTracking';
import { getLoggedInAdminEmail, hasEmailAccess, useEmailAccessConfig } from '../../../../../utils/emailAccessConfig';
import FindTeacher from "../findTeacher";
import axios from 'axios';
import { BLAZE_BASEURL } from '../../../../../Api/api-constant';

const INSTRUMENT_CATEGORIES = {
    keyboard: {
        name: "Keyboard",
        models: {
            yamaha_psr_f52: {
                name: "Yamaha Psr F52",
                subCategory: "",
                price: 9990,
                discountedPrice: 5199,
                images: [
                    "https://d269h6lxjde1tq.cloudfront.net/instrument/yamaha_psr_f52.png"
                ]
            },
            casio_cts_200: {
                name: "Casio CTS 200",
                subCategory: "",
                price: 10495,
                discountedPrice: 5199,
                images: [
                    "https://d269h6lxjde1tq.cloudfront.net/instrument/casio_cts_200.png"
                ]
            }
        }
    },
    guitar: {
        name: "Guitar",
        models: {
            cort_ad300_op: {
                name: "Cort AD300 OP",
                subCategory: "Acoustic guitar",
                price: 8511,
                discountedPrice: 4199,
                images: [
                    "https://d269h6lxjde1tq.cloudfront.net/instrument/cort_ad300_op.png"
                ]
            },
            yamaha_f280: {
                name: "Yamaha F280",
                subCategory: "Acoustic guitar",
                price: 8790,
                discountedPrice: 4199,
                images: [
                    "https://d269h6lxjde1tq.cloudfront.net/instrument/yamaha_f280.png"
                ]
            },
            yamaha_gl1: {
                name: "Yamaha GL1",
                subCategory: "Guitalele for kids",
                price: 8390,
                discountedPrice: 4199,
                images: [
                    "https://d269h6lxjde1tq.cloudfront.net/instrument/yamaha_gl1.png"
                ]
            }
        }
    }
};

const getInstrumentCategory = (category: any) => {
    return category === "guitar" ? INSTRUMENT_CATEGORIES.guitar : INSTRUMENT_CATEGORIES.keyboard;
};

const getCategoryModels = (category: any) => {
    return getInstrumentCategory(category).models as any;
};

const getInstrumentModel = (category: any, modelKey: any) => {
    const models = getCategoryModels(category);
    if (modelKey && models[modelKey]) {
        return models[modelKey];
    }
    const firstModelKey = Object.keys(models)[0];
    return models[firstModelKey];
};

const buildInstrumentFromModel = (category: any, modelKey: any, enabled = false) => {
    const models = getCategoryModels(category);
    const model = getInstrumentModel(category, modelKey);
    const resolvedModelKey = modelKey && models[modelKey]
        ? modelKey
        : Object.keys(models)[0];
    return {
        enabled,
        category: category === "guitar" ? "guitar" : "keyboard",
        model: resolvedModelKey,
        name: model.name,
        subCategory: model.subCategory || "",
        price: model.price,
        discountedPrice: model.discountedPrice,
        images: model.images || []
    };
};

const COMBO_INSTRUMENT_ELIGIBLE_COURSE_IDS = ["509", "517", "505"];
const KEYBOARD_DEFAULT_COURSE_ID = "509";

const getDefaultInstrumentCategoryForCourse = (courseId: any) => {
    return String(courseId) === KEYBOARD_DEFAULT_COURSE_ID ? "keyboard" : "guitar";
};

const getDefaultInstrumentForCourse = (courseId: any) => {
    return buildInstrumentFromModel(getDefaultInstrumentCategoryForCourse(courseId), null, false);
};

const enforceInstrumentCategoryForCourse = (instrumentData: any, courseId: any) => {
    if (!instrumentData || !courseId || !COMBO_INSTRUMENT_ELIGIBLE_COURSE_IDS.includes(String(courseId))) {
        return instrumentData;
    }
    const allowedCategory = getDefaultInstrumentCategoryForCourse(courseId);
    if (instrumentData.category === allowedCategory) {
        return instrumentData;
    }
    return buildInstrumentFromModel(allowedCategory, null, !!instrumentData.enabled);
};

const getDefaultInstrument = () => buildInstrumentFromModel("keyboard", "yamaha_psr_f52", false);

const isComboInstrumentEligible = (courseId: any, currency: any) => {
    if (!courseId || !currency) return false;
    return COMBO_INSTRUMENT_ELIGIBLE_COURSE_IDS.includes(String(courseId))
        && String(currency).toUpperCase() === "INR";
};

const resolvePackageCurrency = (packageData: any, profileData: any, paymentConfig: any) => {
    return packageData?.currency
        || packageData?.courseData?.[0]?.currency
        || paymentConfig?.timezoneWithCountryName?.[profileData?.timezone]?.code
        || null;
};

const getEffectiveCourseId = (selectedCourse: any, packageData: any) => {
    return selectedCourse || packageData?.courseId || "";
};

const COMBO_INSTRUMENT_PREP_CLASSES = 12;

const isPreparatoryLevel = (level: any) =>
    String(level?.level || "").toLowerCase() === "preparatory";

const buildComboInstrumentPackagePreset = (customPlan: any) => {
    if (!customPlan?.levelList) return customPlan;
    return {
        ...customPlan,
        levelList: customPlan.levelList.map((level: any) => ({
            ...level,
            classCount: isPreparatoryLevel(level) ? COMBO_INSTRUMENT_PREP_CLASSES : 0
        })),
        specialDiscountPercentage: 0,
        additionalDiscountPercentage: 0,
        flashDiscountPercentage: 0,
        discountPercentage: 0
    };
};

const isComboInstrumentPackagePresetApplied = (customPlan: any) => {
    if (!customPlan?.levelList) return false;
    const preparatoryLevel = customPlan.levelList.find(isPreparatoryLevel);
    const hasCorrectClasses = !!preparatoryLevel
        && preparatoryLevel.classCount === COMBO_INSTRUMENT_PREP_CLASSES
        && customPlan.levelList.every((level: any) =>
            isPreparatoryLevel(level) ? level.classCount === COMBO_INSTRUMENT_PREP_CLASSES : !level.classCount
        );
    const hasZeroDiscounts = !+(customPlan.specialDiscountPercentage || 0)
        && !+(customPlan.additionalDiscountPercentage || 0)
        && !+(customPlan.flashDiscountPercentage || 0)
        && !+(customPlan.discountPercentage || 0);
    return hasCorrectClasses && hasZeroDiscounts;
};

const resolveInstrumentDiscountedPrice = (instrumentData: any, model: any) => {
    const price = +instrumentData?.price || +model?.price || 0;
    if (instrumentData?.discountedPrice != null && instrumentData.discountedPrice !== "") {
        return +instrumentData.discountedPrice;
    }
    if (model?.discountedPrice != null) {
        return +model.discountedPrice;
    }
    const discountPercentage = instrumentData?.discountPercentage ?? model?.discountPercentage;
    if (discountPercentage != null && price > 0) {
        return +(price - (price * +discountPercentage) / 100).toFixed(2);
    }
    return price;
};

const normalizeInstrumentFromPackage = (saved: any, courseId?: any) => {
    if (!saved) {
        return courseId ? getDefaultInstrumentForCourse(courseId) : getDefaultInstrument();
    }
    let category = saved.category;
    if (!category) {
        category = saved.type === "guitar" ? "guitar" : "keyboard";
    }
    const instrument = buildInstrumentFromModel(category, saved.model, !!saved.enabled);
    const model = getInstrumentModel(category, saved.model);
    const normalized = {
        ...instrument,
        price: saved.price ?? instrument.price,
        discountedPrice: saved.discountedPrice ?? resolveInstrumentDiscountedPrice(saved, model),
        name: saved.name ?? instrument.name,
        subCategory: saved.subCategory ?? instrument.subCategory,
        images: saved.images?.length ? saved.images : instrument.images
    };
    return courseId ? enforceInstrumentCategoryForCourse(normalized, courseId) : normalized;
};

const getInstrumentModelLabel = (model: any, category?: any) => {
    if (!model) return "";
    const bracketLabel = model.subCategory || (category ? getInstrumentCategory(category).name : "");
    return bracketLabel ? `${model.name} (${bracketLabel})` : model.name;
};

const isFlashDiscountApplicableForPlan = (customPlan: any) => {
    if (!customPlan?.flashDiscountPercentage || !customPlan?.classDateTime) return false;
    const currentDate = new Date();
    const targetDate = new Date(customPlan.classDateTime);
    targetDate.setHours(targetDate.getHours() + 1);
    return targetDate.getTime() - currentDate.getTime() >= 0;
};

const getCourseFinalAmountBeforeInstrument = (customPlan: any) => {
    if (!customPlan) return 0;
    const total = +customPlan.total || 0;
    const specialDiscountAmount = (total * (+customPlan.specialDiscountPercentage || 0) / 100);
    const additionalDiscountAmount = (specialDiscountAmount * (+customPlan.additionalDiscountPercentage || 0) / 100);
    const flashApplicable = isFlashDiscountApplicableForPlan(customPlan);
    const flashDiscountAmount = flashApplicable
        ? (additionalDiscountAmount * (+customPlan.flashDiscountPercentage || 0) / 100)
        : 0;

    if (flashApplicable) {
        return +(total - (flashDiscountAmount + additionalDiscountAmount + specialDiscountAmount)).toFixed(2);
    }
    return +(total - (additionalDiscountAmount + specialDiscountAmount)).toFixed(2);
};

const getInstrumentCalculations = (instrumentData: any, courseTotal = 0) => {
    if (!instrumentData?.enabled) {
        return { price: 0, discountAmount: 0, discountedAmount: 0, discountedPrice: 0, discountPercentage: 0, name: "" };
    }
    const model = getInstrumentModel(instrumentData.category, instrumentData.model);
    const price = +instrumentData.price || model.price;
    const discountedAmount = resolveInstrumentDiscountedPrice(instrumentData, model);
    const discountAmount = +(price - discountedAmount).toFixed(2);
    const combinedTotal = +courseTotal + price;
    const discountPercentage = combinedTotal > 0
        ? Math.round((discountAmount / combinedTotal) * 100)
        : 0;
    return {
        price,
        discountAmount,
        discountedAmount,
        discountedPrice: discountedAmount,
        discountPercentage,
        name: getInstrumentModelLabel(
            { name: instrumentData.name || model.name, subCategory: instrumentData.subCategory || model.subCategory },
            instrumentData.category
        )
    };
};

const buildCourseAndInsDiscount = (customPlan: any, instrumentData: any) => {
    if (!instrumentData?.enabled) return null;
    const courseTotal = getCourseFinalAmountBeforeInstrument(customPlan);
    const instrumentCalc = getInstrumentCalculations(instrumentData, courseTotal);
    return { discountPercentage: instrumentCalc.discountPercentage };
};

// Discount policy for DEMO students on the ONLINE payment link (INR / domestic only):
//   < 12 classes   -> max 15% special discount
//   12–23 classes  -> max 20% special discount
//   24+ classes    -> no discount limit
// The admins below may apply an extra 10% on top of a capped tier.
// Only the Special Discount field is used in this flow (Additional/Flash columns are hidden).
// Email lists: utils/emailAccessConfig.ts → demoOnlineDiscountAllowlist / demoOnlineDiscountExempt

const getDemoSpecialDiscountCap = (classCount: number, isAllowlisted: boolean) => {
    if (classCount >= 25) return Infinity; // 25+ classes: no discount restriction
    const cap = classCount >= 13 ? 20 : 15;
    return isAllowlisted ? cap + 10 : cap;
};

const MAX_SPLIT_DISCOUNT = 90;

const SPLIT_CURRENCY_SYMBOLS: Record<string, string> = {
    INR: "₹", USD: "$", EUR: "€", GBP: "£", AUD: "A$", CAD: "C$", SGD: "S$", AED: "AED ",
};

const formatSplitMoney = (value: number, currency?: string) => {
    const code = String(currency || "INR").toUpperCase();
    const symbol = SPLIT_CURRENCY_SYMBOLS[code] ?? `${code} `;
    const locale = code === "INR" ? "en-IN" : "en-US";
    return `${symbol}${Math.round(+value || 0).toLocaleString(locale)}`;
};

const CustomPayment: React.FC<any> = ({ studentId, profileData, selectedPackage, saveCallBack, isExternal, isFree, isSplit, setPaymentModal }) => {
    const removeManual = process.env.REACT_APP_ALWAYS_INCLUDE_TEST_TEACHERS === 'true';
    useEmailAccessConfig();
    const config = useContext(ConfigContext);
    const paymentConfig = useContext(PaymentConfigContext);
    const courses = paymentConfig?.onlineCourseAcademic2 || [];
    const courseLevels = paymentConfig?.onlineCourseAcademic2.map((course: any) =>
        course.levels.map((level: any) => {
            let courseName;
            if (course.name.toLowerCase().includes("karaoke")) {
                courseName = course.name
            } else {
                courseName = course.name + " - " + level.name;
            }
            return {
                ...level,
                courseName: courseName,
                courseId: course.paidCourseId
            };
        })
    ).flat();
    const [selectedCourse, setSelectedCourse] = useState<string>("");
    const [teachers, setTeachers] = useState<any[]>([]);
    const [dropdownTeacherId, setDropdownTeacherId] = useState<string>("");
    const [findTeacherId, setFindTeacherId] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [splitParts, setSplitParts] = useState<number>(2);
    // editing an existing part rather than creating a group
    const isSplitEdit = Boolean(isSplit && selectedPackage?.split_package_id);
    const [splitPreview, setSplitPreview] = useState<any | null>(null);
    const [previewLoading, setPreviewLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [modal, setModal] = useState<boolean>(false);
    const [packageData, setPackageData] = useState<any>(null);
    // currency the split preview renders in - the API returns it on the package
    const splitCurrency = packageData?.customPlan?.currency || packageData?.courseData?.[0]?.currency || packageData?.currency;
    const [walletDetails, setWalletDetails] = useState<any>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [membership, setMembership] = useState<string | null>(null); // Allow null state
    const [membershipAmount, setMembershipAmount] = useState<any>(0); // Allow null state
    const [freeClassReason, setFreeClassReason] = useState<any>("");
    const [freeClassReasonValue, setFreeClassReasonValue] = useState<any>("");
    const [includeTestTeachers, setIncludeTestTeachers] = useState<boolean>(false);
    const [includeResignedTeachers, setIncludeResignedTeachers] = useState<boolean>(false);
    const [findTeacherModal, setFindTeacherModal]= useState<boolean>(false);
    const [studentCourses, setStudentCourses] = useState<any[]>([]);
    //externalPayment
    const [refNumber, setRefNumber] = useState<string>("");
    const [paymentMode, setPaymentMode] = useState<string>("Cash");
    const [paymentModeValue, setPaymentModeValue] = useState<string>("Cash");
    const [skipInvoice, setSkipInvoice] = useState<boolean>(false);
    const [enableEMI, setEnableEMI] = useState<boolean>(false);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    
    // Gift card states
    const [addGiftCard, setAddGiftCard] = useState<boolean>(false);
    const [selectedGiftCardOption, setSelectedGiftCardOption] = useState<string>("2_classes");
    const [emailValidationError, setEmailValidationError] = useState<string>("");
    const [giftCardCode, setGiftCardCode] = useState<string>("");
    const [giftCardError, setGiftCardError] = useState<string>("");
    const [giftCardLoading, setGiftCardLoading] = useState<boolean>(false);
    const [appliedGiftCard, setAppliedGiftCard] = useState<any>(null);
    const giftCardVerifiedRef = useRef<string | null>(null);

    // Sales person shown on the transaction. Defaults to the logged-in agent; an agent
    // raising a link for a colleague's student types that colleague's name instead.
    // Only an edited name is sent (as customSalesAgentName) so the attribution API
    // still decides the sales agent when nobody overrides it.
    const loggedInSalesName = (() => {
        const p = JSON.parse(localStorage.profileData || "{}");
        return `${p?.firstName || ""} ${p?.lastName || ""}`.trim();
    })();
    const [salesAgentNameInput, setSalesAgentNameInput] = useState<string>(
        selectedPackage?.customSalesAgentName || loggedInSalesName
    );
    const getCustomSalesAgentName = () => {
        const name = salesAgentNameInput.trim();
        if (!name) return "";
        return name !== loggedInSalesName || name === selectedPackage?.customSalesAgentName ? name : "";
    };

    // Combo instrument states
    const [instrument, setInstrument] = useState(getDefaultInstrument());

    // Gift card configuration
    const giftCardConfig = {
        "2_classes": { classes: 2, prices: { INR: 1499, USD: 49 } },
        "4_classes": { classes: 4, prices: { INR: 2499, USD: 99 } },
        "12_classes": { classes: 12, prices: { INR: 7499, USD: 249 } }
    };

    
    console.log("packageData",packageData)

    // Resolve the DEMO online-payment-link discount rule for the current package + logged-in admin.
    // Only applies to the online link (not external/record-payment) for domestic (INR) demo students.
    const getDemoDiscountRule = () => {
        const isDemoStudent = ["studentType", "userType"].some(
            (key) => String(profileData?.[key] || "").trim().toUpperCase() === "DEMO"
        );
        const currency = String(
            packageData?.currency || packageData?.courseData?.[0]?.currency || ""
        ).toUpperCase();
        const restricted = !isExternal && isDemoStudent && currency === "INR";
        const classCount = (packageData?.customPlan?.levelList || []).reduce(
            (sum: number, level: any) => sum + (+level.classCount || 0), 0
        );
        const adminEmail = getLoggedInAdminEmail();
        const maxSpecialDiscount = hasEmailAccess("demoOnlineDiscountExempt", adminEmail)
            ? Infinity
            : getDemoSpecialDiscountCap(classCount, hasEmailAccess("demoOnlineDiscountAllowlist", adminEmail));
        return { restricted, maxSpecialDiscount, classCount };
    };

    // Cleanup debounce timer on component unmount
    const fetchStudentCourses = async () => {
        const response = await fetchStudentCoursesAPI(studentId);
        setStudentCourses(response);    
    }
    useEffect(() => {
        fetchStudentCourses();
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);
    

    useEffect(() => {
        if (!selectedPackage) {
            const fetchWalletPoints = async () => {
                const walletDetail = await walletPoints(profileData.userId, profileData.userType.toLowerCase());
                setWalletDetails(walletDetail);
            }
            fetchWalletPoints();
        }
    }, [profileData]);

    useEffect(() => {
        if (!selectedCourse) return;

        const fetchTeachers = async () => {
            setLoading(true);
            setError(null);

            try {
                const courseId = selectedCourse;
                // Use a boolean constant from env to always include type: 'test' if set
                const alwaysIncludeTestTeachers = process.env.REACT_APP_ALWAYS_INCLUDE_TEST_TEACHERS === 'true';
                const payload = {
                    page: 0,
                    size: 500,
                    courseId,
                    ...((includeTestTeachers || alwaysIncludeTestTeachers) && { type: 'test' }),
                    ...(includeResignedTeachers && { resigned: true })
                };
                console.log('includeTestTeachers:', includeTestTeachers, 'alwaysIncludeTestTeachers:', alwaysIncludeTestTeachers);

                const response = await  newFetchTeacherByCourseId( payload);
                if (response.data && Array.isArray(response.data.content)) {
                    setTeachers(response.data.content);
                } else {
                    setError("Unexpected API response format.");
                    setTeachers([]);
                }
            } catch (error) {
                console.error("Error fetching teachers:", error);
                setError("Failed to fetch teachers. Please try again.");
                setTeachers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTeachers();
    }, [selectedCourse, includeTestTeachers, includeResignedTeachers]);

    const handleSelection = (value: string) => {
        const currency = packageData?.currency || packageData?.courseData?.[0]?.currency;
        if (instrument.enabled && isComboInstrumentEligible(selectedCourse, currency)) return;
        setMembership(value);
    };

    // A split part keeps its agreed rate in specialDiscountPercentage, but this form
    // reads Discount(%) from discountPercentage - and createExternalPaymentRequest
    // prices every course from that same field. Left unmapped the field shows 0 and
    // the payment records the full undiscounted price instead of the instalment.
    const withSplitDiscountMapped = (pkg: any) => {
        const rate = +(pkg?.customPlan?.specialDiscountPercentage || 0);
        if (!pkg?.split_package_id || !pkg?.customPlan || !rate) return pkg;
        return {
            ...pkg,
            customPlan: {
                ...pkg.customPlan,
                discountPercentage: rate,
                specialDiscountPercentage: 0,
            },
        };
    };

    const calSelectedPackage = async () => {
        const walletDetail = await walletPoints(profileData.userId, profileData.userType.toLowerCase());
        setWalletDetails(walletDetail);
        if (!selectedPackage.customPlan) {
            profileData.classDateTime = selectedPackage.classDateTime || "";
            createNewPackage(profileData, selectedPackage.courseId, walletDetail);
            setPackageData(selectedPackage);
        } else {
            // split parts never apply the wallet (the backend zeroes it), so don't preview a deduction
            setPackageData(calculetePackage({
                ...withSplitDiscountMapped(selectedPackage),
                ...(isSplit ? { removeWallet: true } : {}),
            }, walletDetail) as any);
        }
    }
    useEffect(() => {
        if (selectedPackage) {
            setEnableEMI(
                selectedPackage?.enableEMI ??
                (selectedPackage?.disableEMI != null ? !selectedPackage.disableEMI : false)
            );
            setSelectedCourse(String(selectedPackage.courseId));
            setDropdownTeacherId(selectedPackage.teacherId);
            setFindTeacherId("");
            if(selectedPackage.membership){
                setMembership(selectedPackage.membership);
                setMembershipAmount(selectedPackage.membershipExtraAmount || 0);
                //const [membership, setMembership] = useState<string | null>(null); // Allow null state
                //const [membershipAmount, setMembershipAmount] = useState<any>(0); // Allow null state
            }else if(selectedPackage.membership == ""){
                setMembership("");  // handle not applicable case
                setMembershipAmount(0);
            }
            
            // Handle gift card edit scenario
            if (selectedPackage.giftCard) {
                setAddGiftCard(selectedPackage.giftCard.addGiftCard);
                setSelectedGiftCardOption(selectedPackage.giftCard.selectedOption || "2_classes");
            } else {
                setAddGiftCard(false);
                setSelectedGiftCardOption("2_classes");
            }

            // Handle combo instrument edit scenario
            const packageCurrency = selectedPackage.currency || selectedPackage.courseData?.[0]?.currency;
            const restoredInstrument = normalizeInstrumentFromPackage(
                selectedPackage.instrumentCombo ?? selectedPackage.instrument,
                selectedPackage.courseId
            );
            if (!isComboInstrumentEligible(selectedPackage.courseId, packageCurrency) && restoredInstrument.enabled) {
                setInstrument(getDefaultInstrumentForCourse(selectedPackage.courseId));
            } else {
                setInstrument(restoredInstrument);
            }
            
            // Handle applied gift card code
            if (selectedPackage.giftVoucher) {
                // Set the code initially, verification will happen after packageData is set
                setGiftCardCode(selectedPackage.giftVoucher);
            } else {
                setGiftCardCode("");
                setGiftCardError("");
                setAppliedGiftCard(null);
            }
            
            calSelectedPackage();

        } else {
            setEnableEMI(false);
            setInstrument(getDefaultInstrument());
        }

    }, [selectedPackage]);

    const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const courseId = e.target.value;
        setSelectedCourse(courseId);
        setDropdownTeacherId("");
        // Clear gift card when course changes
        setGiftCardCode("");
        setGiftCardError("");
        setAppliedGiftCard(null);
        setInstrument(getDefaultInstrumentForCourse(courseId));
        createNewPackage(profileData, courseId, walletDetails);
    };

    // Handle teacher selection from dropdown
    const handleTeacherChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setDropdownTeacherId(e.target.value);
        setFindTeacherId("");
    };

    const createNewPackage = async (profileData: any, courseId: any, walletDetails: any) => {
        if (!profileData || !courseId) return;

        let name, email, contact, state, gender, teacherName, ageGroup, demoBookedCourseId, demoBookedCourseName, teacherId, userId, userType, countryCode, timeZone, classDateTime, zohoSubscriptionCustomerId;
        userId = profileData.userId;
        userType = profileData.userType;
        name = `${(profileData.firstName || "").trim()}${profileData.lastName ? ` ${(profileData.lastName || "").trim()}` : ""}`;
        email = profileData.emailId;
        contact = profileData.mobileNumber;
        state = profileData.state;
        gender = profileData.gender;
        ageGroup = profileData.ageGroup;
        userId = profileData.userId;
        userType = profileData.userType;
        countryCode = profileData.countryCode;
        timeZone = profileData.timezone;
        studentId = +profileData.studentId;

        const courseDetails = courses.find((e: any) => e.paidCourseId == courseId);
        if (!courseDetails?.levels?.length) {
            toast.error("Course details not found");
            return;
        }
        let levelIds = ([] as any);
        const courseList = courseDetails.levels.map((lev: any) => {
            levelIds.push(lev.levelId);
            return {
                courseId: courseDetails.paidCourseId.toString(),
                levelId: lev.levelId.toString(),
                courseName: courseDetails.name,
                level: lev.name,
                classesToPurchase: 0,
                //zohoSubscriptionId: zohoSubscriptionCustomerId,
                //classesAvailableToBuy: lev.classsize,
                totalClassesInCourse: lev.classsize.toString()
            };
        });
        //const walletDetails = await walletPoints(userId, userType.toLowerCase());
        const priceCCObj = paymentConfig?.timezoneWithCountryName?.[profileData.timezone];
        if (!priceCCObj?.code) {
            toast.error("Unable to determine pricing for student timezone");
            return;
        }
        const priceDetails = await getCoursePrice(levelIds.map(Number), priceCCObj.code, priceCCObj.country.toLowerCase());
        let accountDetails = [] as any;
        try {
            const promises = levelIds.map(async (levelId: any) => {
                const data = await getAccountDetails(levelId);
                if (data.success) {
                    let accData = data.data;
                    console.log("data.data.Item-------------", accData);
                    if (isFree) {
                        accData.item_id = accData.item_id_free ? accData.item_id_free : "";
                    }

                    accData.item_id = accData.item_id ? accData.item_id.toString() : "";
                    accData.account_id = accData.account_id ? accData.account_id.toString() : "";
                    accData.item_id_free = accData.item_id_free ? accData.item_id_free.toString() : "";
                    accData.item_id_extra = accData.item_id_extra ? accData.item_id_extra.toString() : "";
                    accData.account_id_extra = accData.account_id_extra ? accData.account_id_extra.toString() : "";
                    accountDetails.push(accData);
                }
            });
            await Promise.all(promises);
        } catch (error) {
            console.error('[===========]', error);
        }
        let mergedData;
        if (courseList.length && priceDetails && priceDetails.data) {
            // if (accountDetails.length == levelIds.length && courseList.length && priceData.data && priceData.data.data) {
            mergedData = courseList.map((course: any) => {
                const levelId = course.levelId.toString();
                const priceInfo = priceDetails.data.find((price: any) => price.levelId === Number(levelId));
                const accountInfo = accountDetails.find((account: any) => account.id === levelId);

                return {
                    ...accountInfo,
                    ...course,
                    unit_price: priceInfo ? priceInfo.price : null,
                    priceWithoutGST: priceInfo && priceInfo.currency == 'INR' ? (priceInfo.price / 1.18).toString() : priceInfo.price.toString(),
                    priceWithoutGSTRound: priceInfo && priceInfo.currency == 'INR' ? +((priceInfo.price / 1.18).toFixed(2)) : +(priceInfo.price.toFixed(2)),
                    gst: priceInfo && priceInfo.currency == 'INR' ? 1.18 : 0,
                    currency: priceInfo ? priceInfo.currency : priceCCObj.code,
                    currencySymbol: priceInfo ? priceInfo.currencySymbol : null,
                    symbolDirection: priceInfo ? priceInfo.symbolDirection : null,
                };
            });
        }

        if (!mergedData?.length) {
            toast.error("Unable to load course pricing");
            return;
        }

        const customPlan = isExternal ? createExternalCustomPlan(mergedData, walletDetails) : createCustomPlan(mergedData, walletDetails);

        const packageData = {
            ...selectedPackage || {},
            ...{ name, email, contact, state, courseId, gender, ageGroup, studentId, demoBookedCourseId, demoBookedCourseName, teacherId, teacherName, userId, userType, countryCode, timeZone, classDateTime },
            referralData: walletDetails ? (walletDetails || null) : null,
            planList: [],
            isFree: isFree ? true : false,
            currency: mergedData[0].currency || priceCCObj.code,
            currencySymbol: mergedData[0].currencySymbol,
            symbolDirection: mergedData[0].symbolDirection,
            customPlan: customPlan,
            classDateTime:profileData.classDateTime || "",
            enableEMI: selectedPackage?.enableEMI ??
                (selectedPackage?.disableEMI != null ? !selectedPackage.disableEMI : false),
            // expiryDate: selectedDate,
            courseData: mergedData || [],
        }
        if(isExternal){
            packageData.removeWallet = true;
        }
        if (isSplit) {
            // split parts never apply the wallet (the backend zeroes it), so don't preview a deduction
            setPackageData(calculetePackage({ ...packageData, removeWallet: true }, walletDetails) as any);
            return;
        }
        setPackageData(packageData);
    }

    const handleClassCount = (level: any, value: string | number) => {
        setSplitPreview(null); // the shown split no longer matches these counts
        const currency = packageData?.currency || packageData?.courseData?.[0]?.currency;
        if (instrument.enabled && isComboInstrumentEligible(selectedCourse, currency)) return;
        let sanitizedValue = typeof value === "string"
            ? value.replace(/[^0-9]/g, '')  // Remove non-numeric characters
            : value;
        const newLevelList = packageData.customPlan.levelList.map((l: any, i: number) => {
            if (l.levelId === level.levelId) {
                return {
                    ...l,
                    classCount: +sanitizedValue,
                };
            }
            return l;
        });

        const newCustomPlan = {
            ...packageData.customPlan,
            levelList: newLevelList,
        };

        setPackageData(calculetePackage({
            ...packageData,
            customPlan: newCustomPlan,
        }, walletDetails) as any);

    };

    // const handleDiscountChange = (type: string, value: string | number) => {
    //     let sanitizedValue = typeof value === "string"
    //         ? value.replace(/[^0-9]/g, '') // Remove non-numeric characters
    //         : value;
    //     const newCustomPlan = {
    //         ...packageData.customPlan,
    //         [type]: +sanitizedValue,
    //     };

    //     setPackageData(calculetePackage({
    //         ...packageData,
    //         customPlan: newCustomPlan,
    //     }, walletDetails) as any);
    // }
    const handleDiscountChange = (type: string, value: string) => {
        setSplitPreview(null); // the shown split no longer matches this discount
        const currency = packageData?.currency || packageData?.courseData?.[0]?.currency;
        if (instrument.enabled && isComboInstrumentEligible(selectedCourse, currency)) return;
        // Allow only digits and one optional dot
        if (!/^\d*\.?\d*$/.test(value)) return;
        // A split is priced off this rate, so keep it inside a sane bound
        if (isSplit && type === 'specialDiscountPercentage' && +value > MAX_SPLIT_DISCOUNT) return;
      
        const newCustomPlan = {
          ...packageData.customPlan,
          [type]: (+value || 0), // Store as string for now, let validation/submit convert to number
        };
       setPackageData(calculetePackage({
            ...packageData,
            customPlan: newCustomPlan,
        }, walletDetails) as any);
      };

    // --- split payment -------------------------------------------------------
    // Reuses this whole form; only the parts selector and the submit differ.
    // Preview and create share this payload so the preview can never drift from
    // what actually gets created.
    // Each split part is saved as a normal payment link, so this sends the same
    // packageData the "Save Package" button sends, plus the two split controls.
    const buildSplitPayload = () => {
        const levels = (packageData?.customPlan?.levelList || []).filter((l: any) => +l.classCount > 0);
        if (!levels.length) {
            toast.error("Add class counts before creating a split payment.");
            return null;
        }
        const profile = JSON.parse(localStorage.profileData || "{}");
        const selectedTeacher = findTeacherId || dropdownTeacherId || "";
        return {
            ...packageData,
            studentId,
            courseId: selectedCourse,
            teacherId: selectedTeacher,
            teacherName: getTeacherNameById(selectedTeacher),
            expiryDate: selectedDate ? selectedDate.toISOString().split('T')[0] : packageData?.expiryDate || null,
            createdBy: profile?.firstName || "",
            createdById: profile?.userId || "",
            createdByRole: profile?.role || "",
            createdByMobile: profile?.mobileNumber || "",
            createdByCountryCode: profile?.countryCode || "",
            // the sales person who raised the split, in one readable set of fields
            salesPersonUserId: profile?.userId || "",
            salesPersonName: `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim(),
            salesPersonMobile: `${profile?.countryCode || ""}${profile?.mobileNumber || ""}`,
            salesPersonEmail: profile?.emailId || "",
            customSalesAgentName: getCustomSalesAgentName(),
            membership: membership || "",
            membershipExtraAmount: +membershipAmount || 0,
            parts: splitParts,
            discountPercent: +(packageData?.customPlan?.specialDiscountPercentage || 0),
        };
    };

    const previewSplit = async () => {
        const payload = buildSplitPayload();
        if (!payload) return;
        setPreviewLoading(true);
        try {
            const res = await getSplitPreview({
                parts: payload.parts,
                discountPercent: payload.discountPercent,
                classes: (payload.customPlan?.levelList || [])
                    .filter((l: any) => +l.classCount > 0)
                    .map((l: any) => ({ name: l.level, count: +l.classCount, price: +l.unit_price })),
            });
            const body = res?.data;
            if (body?.success && body?.data?.parts?.length) {
                setSplitPreview(body.data);
            } else {
                setSplitPreview(null);
                toast.error("Could not build preview");
            }
        } catch (e) {
            console.error("splitPreview failed", e);
            setSplitPreview(null);
            toast.error("Could not build preview");
        }
        setPreviewLoading(false);
    };

    const updateSplitPart = async () => {
        const levels = (packageData?.customPlan?.levelList || []).filter((l: any) => +l.classCount > 0);
        if (!levels.length) {
            toast.error("Add class counts before saving.");
            return;
        }
        setLoading(true);
        try {
            const profile = JSON.parse(localStorage.profileData || "{}");
            const res = await updateSplitPackage({
                ...packageData,
                id: selectedPackage.id,
                createdOn: selectedPackage.createdOn,
                split_package_id: selectedPackage.split_package_id,
                updatedBy: profile?.firstName || "",
                updatedById: profile?.userId || "",
                updatedByRole: profile?.role || "",
                updatedByMobile: profile?.mobileNumber || "",
                updatedByCountryCode: profile?.countryCode || "",
                customSalesAgentName: getCustomSalesAgentName(),
            });
            if (res?.data?.success) {
                trackEvent(MixpanelEvents.SPLIT_PAYMENT_UPDATED, {
                    split_group_id: selectedPackage.split_group_id,
                    split_package_id: selectedPackage.split_package_id,
                    split_part: selectedPackage.split_part,
                    studentId,
                    courseId: selectedCourse,
                    fromPayable: selectedPackage.split_total,
                    toPayable: res.data?.data?.split_total,
                });
                toast.success("Payment link updated");
                if (saveCallBack) saveCallBack();
            } else {
                toast.error(res?.data?.message || "Could not update the payment link");
            }
        } catch (e: any) {
            console.error("updateSplitPackage failed", e);
            toast.error(e?.response?.data?.message || "Could not update the payment link");
        }
        setLoading(false);
    };

    const createSplitFromPlan = async () => {
        const payload = buildSplitPayload();
        if (!payload) return;
        if (!selectedCourse) {
            toast.error("Please select a course.");
            return;
        }
        if (membership == null) {
            toast.error("Please select plan details!");
            return;
        }
        setLoading(true);
        try {
            const res = await createSplitPackage(payload);
            const body = res?.data;
            if (body?.success && body?.data?.parts?.length) {
                trackEvent(MixpanelEvents.SPLIT_PAYMENT_CREATED, {
                    split_group_id: body.data.split_group_id,
                    studentId,
                    courseId: selectedCourse,
                    courseName: packageData?.courseData?.[0]?.courseName || "",
                    parts: body.data.parts.length,
                    discountPercent: payload.discountPercent,
                    partAmounts: body.data.parts.map((pt: any) => pt.split_total),
                    membership: payload.membership,
                });
                toast.success(`${body.data.parts.length} payment links created`);
                if (saveCallBack) saveCallBack();
            } else {
                toast.error(body?.message || "Could not create split payment");
            }
        } catch (e: any) {
            console.error("createSplitPackage failed", e);
            toast.error(e?.response?.data?.message || "Could not create split payment");
        }
        setLoading(false);
    };

    const savePackageData = async () => {
        // Enforce the DEMO online-payment-link discount cap before saving.
        const demoDiscount = getDemoDiscountRule();
        if (demoDiscount.restricted) {
            const specialDiscount = +(packageData?.customPlan?.specialDiscountPercentage || 0);
            if (specialDiscount > demoDiscount.maxSpecialDiscount) {
                toast.error(`Maximum discount for ${demoDiscount.classCount} classes is ${demoDiscount.maxSpecialDiscount}%.`);
                return;
            }
            // Additional & Flash discounts are hidden in this flow — never carry them into the saved plan.
            if (packageData?.customPlan) {
                packageData.customPlan.additionalDiscountPercentage = 0;
                packageData.customPlan.flashDiscountPercentage = 0;
            }
        }
        setLoading(true)
    if (packageData.freeCourse && packageData.freeCourse.classesToPurchase) {
            packageData.freeCourse.classesToPurchase = +packageData.freeCourse.classesToPurchase;
        }
        if (packageData.freeCourse && !packageData.freeCourse.classesToPurchase) {
            toast.error('Please add class count for free course');
            setLoading(false);
            return;
        }
        if (selectedDate) {
            packageData.expiryDate = selectedDate.toISOString().split('T')[0];
        }
        if(membership){
            packageData.membership = membership;
            packageData.membershipExtraAmount = +membershipAmount || 0;
        }else{
            packageData.membership = membership == "" ? "" : null;
            packageData.membershipExtraAmount = 0;
        }
        
        // Handle gift card data
        if (addGiftCard && selectedGiftCardOption && !emailValidationError) {
            packageData.giftCard = {
                addGiftCard: true,
                classCount: giftCardConfig[selectedGiftCardOption as keyof typeof giftCardConfig].classes,
                classPrice: giftCardConfig[selectedGiftCardOption as keyof typeof giftCardConfig].prices[(packageData?.courseData?.[0]?.currency as keyof typeof giftCardConfig["2_classes"]["prices"]) || 'INR'],
                selectedOption: selectedGiftCardOption,
                email: profileData.emailId
            };
        } else {
            packageData.giftCard = null;
        }

        const saveCurrency = packageData.currency || packageData.courseData?.[0]?.currency;
        const comboInstrumentActive = instrument.enabled && isComboInstrumentEligible(selectedCourse, saveCurrency);
        if (comboInstrumentActive) {
            packageData.instrumentCombo = { ...instrument };
            packageData.courseAndInsDiscount = buildCourseAndInsDiscount(packageData.customPlan, instrument);
            delete packageData.instrument;
            packageData.enableEMI = false;
            packageData.removeWallet = true;
            packageData.giftCard = null;
            packageData.giftVoucher = null;
            packageData.giftCardDiscount = null;
            packageData.membership = "";
            packageData.membershipExtraAmount = 0;
            if (packageData.customPlan) {
                packageData.customPlan.walletAmount = 0;
                packageData.customPlan.walletLabel = "";
                packageData.customPlan.myCash = 0;
                packageData.customPlan.artiumCash = 0;
            }
        } else {
            packageData.instrumentCombo = null;
            packageData.courseAndInsDiscount = null;
            delete packageData.instrument;
        }

        if(membership == null){
            toast.error('Please select plan details!');
            setLoading(false);
            return;
        }
        packageData.enableEMI = comboInstrumentActive ? false : enableEMI;
        // packageData.customPlan
        let selectedTeacher = findTeacherId || dropdownTeacherId;
        if(!selectedTeacher && studentCourses.some((course: any) => (!course.isDemoCourse && course.courseId == selectedCourse))){
            selectedTeacher = studentCourses.find((course: any) => (!course.isDemoCourse && course.courseId == selectedCourse))?.teacherId;
            // toast.error('Please select teacher!');
            // setLoading(false);
            // return;
        }
        packageData.teacherId = selectedTeacher;
        packageData.teacherName = getTeacherNameById(selectedTeacher);
        let lList = packageData.customPlan.levelList.filter((level: any) => +level.classCount);
       
        if (lList.length) {
            //packageData.customPlan.levelList = lList;
            packageData.customPlan.packageInfo = `Includes ${packageData.customPlan.levelList.map((item: any) => item.classCount + ' ' + item.level).join(" and ")} classes`;
            packageData.createdBy = JSON.parse(localStorage.profileData || "{}")?.firstName || "";
            let mNumber = JSON.parse(localStorage.profileData || "{}")?.mobileNumber || "";
            let cCode = JSON.parse(localStorage.profileData || "{}")?.countryCode || "";
            packageData.createdByMobile = mNumber ? mNumber : "";
            packageData.createdByCountryCode = cCode ? cCode : "";
            packageData.customSalesAgentName = getCustomSalesAgentName();
            let res = await savePackage(packageData);

            if ((res as any)?.data?.success) {
                (saveCallBack && saveCallBack());
                toast.success("Package saved successfully!");
                setLoading(false);
                // if (fetchCourses) fetchCourses();
            }

            trackEvent(MixpanelEvents.PAYMENT_PACKAGE, {
                teacherId: selectedTeacher,
                studentCourseIds: (studentCourses || []).map((course: any) => course.courseId) || "",
                studentTeacherIds: (studentCourses || []).map((course: any) => course.teacherId) || "",
                teacherName: packageData.teacherName,
                courseId: selectedCourse,
                studentId: packageData.studentId,
                studentName: packageData.name,
                Date: selectedDate,
                type: "Payment Link Edit Package",
              });
            

        } else {
            setLoading(false);
            toast.error("Please add classes!");
        }
    }

    const debouncedSavePackageData = () => {
        // Clear any existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Prevent multiple rapid clicks
        if (loading) {
            return;
        }

        // Set a debounce timer to prevent double clicks
        debounceTimerRef.current = setTimeout(() => {
            savePackageData();
        }, 1000); // 1000ms debounce delay
    };

    const handleUseWallet = (event: any) => {
        const currency = packageData?.currency || packageData?.courseData?.[0]?.currency;
        if (instrument.enabled && isComboInstrumentEligible(selectedCourse, currency)) return;
        let removeWallet = !event.target.checked;
        setPackageData(calculetePackage({
            ...packageData,
            removeWallet: removeWallet
        }, walletDetails) as any);
    }

    const handleEmiToggle = (checked: boolean) => {
        const currency = packageData?.currency || packageData?.courseData?.[0]?.currency;
        if (checked && instrument.enabled && isComboInstrumentEligible(selectedCourse, currency)) {
            const clearedInstrument = getDefaultInstrumentForCourse(selectedCourse);
            setInstrument(clearedInstrument);
            if (packageData) {
                setPackageData({ ...packageData, instrumentCombo: null, courseAndInsDiscount: null });
            }
        }
        setEnableEMI(checked);
        if (packageData) {
            setPackageData({
                ...packageData,
                enableEMI: checked
            });
        }
        trackEvent(MixpanelEvents.PAYMENT_PACKAGE, {
            type: "Enable EMI Toggle Changed",
            enableEMI: checked,
            studentId: packageData?.studentId || profileData?.studentId || studentId,
            courseId: selectedCourse,
            userName: localStorage.loginUser || "",
            studentName: packageData?.name || profileData?.firstName || ""
        });
    };

    const addFreeCourse = async (e: any) => {

        if (e.target.value) {
            if (packageData.customPlan.levelList.find((y: any) => y.levelId == e.target.value)) {
                toast.warning('Course already added in package list');
                return;
            }
            const clonePackage = { ...{}, ...packageData };
            const selectedCourseLevel = courseLevels.find((z: any) => z.levelId == e.target.value);
            let accountDetail = "";
            // const accountDetailResponse = await getArtiumAccountDetail(e.target.value);
            // if (accountDetailResponse.data && accountDetailResponse.data.data) {
            //   accountDetail = accountDetailResponse.data.data;
            // }
            clonePackage.freeCourse = {
                "courseName": selectedCourseLevel.courseName,
                "teacherId": "",
                "classesToPurchase": 0,
                "discountedPrice": 0,
                "unit_price": 0,
                "pricePerClass": 0,
                "amountBeforeTax": 0,
                "category": selectedCourseLevel.category || "",
                "gst": 0,
                //   "account_id": accountDetail.account_id,
                //   "account_id_prod": accountDetail.account_id,
                //   "account_id_uat": accountDetail.account_id,
                //   "item_id": accountDetail.item_id_free,
                //   "item_id_prod": accountDetail.item_id_free,
                //   "item_id_uat": accountDetail.item_id_free,
                "level": selectedCourseLevel.name,
                "discountedPercentage": 0,
                "levelId": selectedCourseLevel.levelId,
                "courseId": selectedCourseLevel.courseId,
                "trialTeacher": "",
                "price": 0,
                "isFree": true
            }
            setPackageData(clonePackage);
        } else {
            const clonePackage = packageData;
            clonePackage.freeCourse = null;
            setPackageData(clonePackage);
        }
    }

    const addFreeClassCount = (e: any) => {
        const sanitizedValue = typeof e.target.value === "string" ? e.target.value.replace(/^0+/, '') : e.target.value;
        const clonePackage = { ...{}, ...packageData };
        clonePackage.freeCourse.classesToPurchase = +sanitizedValue || "";
        setPackageData(clonePackage);
    }

    const callExternalPayment = async () => {
        let selectedTeacher = findTeacherId || dropdownTeacherId;
        if(!selectedTeacher && studentCourses.some((course: any) => (!course.isDemoCourse && course.courseId == selectedCourse))){
            selectedTeacher = studentCourses.find((course: any) => (!course.isDemoCourse && course.courseId == selectedCourse))?.teacherId;
            // toast.error('Please select teacher!');
            // setLoading(false);
            // return;
        }
        let levelListWithoutFiler = [...[], ...packageData.customPlan.levelList];
        let lList = packageData.customPlan.levelList.filter((level: any) => +level.classCount);
        if (lList.length) {
            packageData.customPlan.levelList = lList;
            let request = createExternalPaymentRequest(profileData, { ...packageData, ...{ refNumber, paymentMode, skipInvoice, selectedTeacher: selectedTeacher, freeClassReason } }) as any;
            request.customSalesAgentName = getCustomSalesAgentName();
            // createExternalPaymentRequest builds a curated payload, so a split part's
            // identity has to be added back or the backend cannot stamp the part paid
            // and the payment link never advances to the next one.
            if (selectedPackage?.split_package_id) {
                const profile = JSON.parse(localStorage.profileData || "{}");
                request.packageId = selectedPackage.package_id;
                request.split_package_id = selectedPackage.split_package_id;
                request.split_group_id = selectedPackage.split_group_id;
                request.split_part = selectedPackage.split_part;
                request.split_parts_total = selectedPackage.split_parts_total;
                request.split_next_payment_link = selectedPackage.split_next_payment_link;
                request.salesPersonUserId = selectedPackage.salesPersonUserId;
                request.salesPersonName = selectedPackage.salesPersonName;
                request.salesPersonMobile = selectedPackage.salesPersonMobile;
                request.salesPersonEmail = selectedPackage.salesPersonEmail;
                request.split_total = selectedPackage.split_total;
                request.performedBy = profile?.firstName || "";
                request.performedById = localStorage.getItem("userId") || "";
                request.performedByRole = profile?.role || "";
            }
            setLoading(true);
            try {
                if(membership){
                    request.membership = membership;
                    request.membershipExtraAmount = +membershipAmount || 0;
                }
                // Add gift card data if applied
                if (packageData.giftVoucher && packageData.giftCardDiscount) {
                    request.giftVoucher = packageData.giftVoucher;
                    request.giftCardDiscount = packageData.giftCardDiscount;
                    // Subtract gift card discount from total price
                    request.totalPrice = Math.max((request.totalPrice - packageData.giftCardDiscount), 0);
                }
                let res = await triggerExternalPayment(request);
                console.log("savePackageData", res);
                if (res.status === 200) {
                    if (selectedPackage?.split_package_id) {
                        trackEvent(MixpanelEvents.SPLIT_PAYMENT_MARKED_EXTERNAL, {
                            split_group_id: selectedPackage.split_group_id,
                            split_package_id: selectedPackage.split_package_id,
                            split_part: selectedPackage.split_part,
                            split_total: selectedPackage.split_total,
                            studentId,
                            courseId: selectedCourse,
                            paymentMode,
                            refNumber,
                        });
                    }
                    toast.success("Class created successfully!");
                    // if (fetchCourses) fetchCourses();

                  
                    trackEvent( MixpanelEvents.PAYMENT_PACKAGE,{
                        type:"Record External Payment Create Class",
                        teacherId: selectedTeacher,
                        studentCourseIds: (studentCourses || []).map((course: any) => course.courseId) || "",
                        studentTeacherIds: (studentCourses || []).map((course: any) => course.teacherId) || "",
                        teacherName: packageData.teacherName,
                        courseId: selectedCourse,
                        Date: selectedDate,
                        studentId: packageData.studentId,
                        studentName: packageData.name,
                    })
                    // if (fetchCourses) fetchCourses();
                    setLoading(false);
                    saveCallBack();
                    // fetchCourses ();
                } else {
                    toast.error("Failed to create class. Please try again.");
                    setLoading(false);
                }
            } catch (e) {
                toast.error("Failed to create class. Please try again.");
                console.log("Error in external payment", e);
                setLoading(false);
            }
        }else{
            setLoading(false);
            toast.error("Please add classes!");
        }
    }

    const debouncedCallExternalPayment = () => {
        // Clear any existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Prevent multiple rapid clicks
        if (loading) {
            return;
        }

        // Set a debounce timer to prevent double clicks
        debounceTimerRef.current = setTimeout(() => {
            callExternalPayment();
        }, 1000); // 1000ms debounce delay
    };

    const calculateDifference = (packageDetails: any) => {
        return isFlashDiscountApplicableForPlan(packageDetails) ? 1 : 0;
    };

    // Helper to get teacher name by id
    const getTeacherNameById = (id: string) => {
        const teacher = teachers.find((t) => t.teacherId === id);
        return teacher ? teacher.teacherName : "";
    };

    // Gift card handlers
    const handleGiftCardCheckboxChange = (checked: boolean) => {
        const currency = packageData?.currency || packageData?.courseData?.[0]?.currency;
        if (checked && instrument.enabled && isComboInstrumentEligible(selectedCourse, currency)) {
            const clearedInstrument = getDefaultInstrumentForCourse(selectedCourse);
            setInstrument(clearedInstrument);
            if (packageData) {
                setPackageData({ ...packageData, instrumentCombo: null, courseAndInsDiscount: null });
            }
        }
        setAddGiftCard(checked);
        if (!checked) {
            setSelectedGiftCardOption("2_classes");
            setEmailValidationError("");
            // Clear gift card data from packageData
            if (packageData) {
                const updatedPackageData = {
                    ...packageData,
                    giftCard: null
                };
                setPackageData(updatedPackageData);
            }
        } else {
            // Check if profile email exists
            validateEmailForGiftCard();
        }
    };

    const validateEmailForGiftCard = () => {
        if (!profileData?.emailId || profileData.emailId.trim() === "") {
            setEmailValidationError("User email is required to add gift card. Please add user email first.");
            setAddGiftCard(false);
            setSelectedGiftCardOption("2_classes");
        } else {
            setEmailValidationError("");
            // Save gift card data to packageData
            saveGiftCardToPackageData();
        }
    };

    const handleGiftCardOptionChange = (option: string) => {
        setSelectedGiftCardOption(option);
        if (addGiftCard && !emailValidationError) {
            saveGiftCardToPackageData(option);
        }
    };

    const saveGiftCardToPackageData = (option?: string) => {
        const giftCardOption = option || selectedGiftCardOption;
        if (packageData && giftCardOption) {
            const giftCardData = {
                addGiftCard: true,
                selectedOption: giftCardOption,
                email: profileData.emailId
            };
            
            const updatedPackageData = {
                ...packageData,
                giftCard: giftCardData
            };
            setPackageData(updatedPackageData);
        }
    };

    const clearComboInstrumentBlockedOptions = () => {
        setEnableEMI(false);
        setAddGiftCard(false);
        setSelectedGiftCardOption("2_classes");
        setEmailValidationError("");
        setGiftCardCode("");
        setGiftCardError("");
        setAppliedGiftCard(null);
        setMembership("");
        setMembershipAmount(0);
    };

    const applyComboInstrumentPackageSettings = (instrumentData: any) => {
        if (!packageData) return;
        clearComboInstrumentBlockedOptions();
        const newCustomPlan = buildComboInstrumentPackagePreset(packageData.customPlan);
        const updatedPackage = calculetePackage({
            ...packageData,
            customPlan: newCustomPlan,
            giftCard: null,
            giftVoucher: null,
            giftCardDiscount: null,
            enableEMI: false,
            removeWallet: true,
            instrumentCombo: { ...instrumentData },
            courseAndInsDiscount: buildCourseAndInsDiscount(newCustomPlan, instrumentData)
        }, walletDetails);
        setPackageData(updatedPackage);
    };

    const syncInstrumentToPackageData = (instrumentData: any) => {
        if (!packageData) return;
        setPackageData({
            ...packageData,
            instrumentCombo: instrumentData.enabled ? { ...instrumentData } : null,
            courseAndInsDiscount: instrumentData.enabled
                ? buildCourseAndInsDiscount(packageData.customPlan, instrumentData)
                : null
        });
    };

    const handleComboInstrumentToggle = (checked: boolean) => {
        const courseId = getEffectiveCourseId(selectedCourse, packageData);
        const updatedInstrument = checked
            ? buildInstrumentFromModel(getDefaultInstrumentCategoryForCourse(courseId), null, true)
            : getDefaultInstrumentForCourse(courseId);
        setInstrument(updatedInstrument);
        if (checked) {
            applyComboInstrumentPackageSettings(updatedInstrument);
        } else {
            syncInstrumentToPackageData(updatedInstrument);
        }
    };

    const handleInstrumentModelChange = (modelKey: any) => {
        const updatedInstrument = buildInstrumentFromModel(instrument.category, modelKey, instrument.enabled);
        setInstrument(updatedInstrument);
        syncInstrumentToPackageData(updatedInstrument);
    };

    // Helper function to get formatted gift card option text
    const getGiftCardOptionText = (optionKey: string) => {
        const config = giftCardConfig[optionKey as keyof typeof giftCardConfig];
        if (!config) return '';
        
        const currency = packageData?.courseData?.[0]?.currency || 'INR';
        const price = config.prices[currency as keyof typeof config.prices] || config.prices.INR;
        const currencySymbol = currency === 'USD' ? 'USD ' : '₹';
        
        return `${config.classes} classes worth ${currencySymbol}${price}`;
    };

    // Function to apply gift card code
    const applyGiftCardCode = async () => {
        if (!giftCardCode.trim()) {
            setGiftCardError("Please enter a gift card code");
            return;
        }

        setGiftCardLoading(true);
        setGiftCardError("");
        
        try {
            const apiUrl = `${BLAZE_BASEURL}/api/gift-cards/code/${giftCardCode.trim()}/active`;
            const response = await axios.get(apiUrl);
            
            if (response.data) {
                if (response.data.active === false) {
                    setGiftCardError("Please enter a valid gift card code");
                    setAppliedGiftCard(null);
                    // Clear gift card data from packageData
                    if (packageData) {
                        const updatedPackageData = {
                            ...packageData,
                            giftVoucher: null,
                            giftCardDiscount: null
                        };
                        setPackageData(updatedPackageData);
                    }
                } else if (response.data.active === true) {
                    // Check if it's a free gift card and if studentId matches
                    if (response.data.giftCard?.isFree === true) {
                        const packageStudentId = packageData?.studentId || profileData?.studentId || studentId;
                        const giftCardStudentId = response.data.giftCard?.studentId;
                        
                        if (packageStudentId && giftCardStudentId && +packageStudentId === +giftCardStudentId) {
                            setGiftCardError("Free gift card not applicable for the same user");
                            setAppliedGiftCard(null);
                            // Clear gift card data from packageData
                            if (packageData) {
                                const updatedPackageData = {
                                    ...packageData,
                                    giftVoucher: null,
                                    giftCardDiscount: null
                                };
                                setPackageData(updatedPackageData);
                            }
                            setGiftCardLoading(false);
                            return;
                        }
                    }
                    
                    setGiftCardError("");
                    setAppliedGiftCard(response.data);
                    
                    // Get package currency
                    const packageCurrency = packageData?.courseData?.[0]?.currency || 'INR';
                    const giftCardCurrency = response.data.giftCard?.currency || 'INR';
                    const classCount = response.data.giftCard?.classCount?.toString() || '4';
                    
                    // Determine discount amount
                    let discountAmount = 0;
                    if (giftCardCurrency === packageCurrency) {
                        // Same currency - use currencyValues directly
                        discountAmount = response.data.giftCard?.currencyValues?.[packageCurrency] || 0;
                    } else {
                        // Different currency - map from giftCardConfig using classCount
                        // Map classCount to config key: 2 -> "2_classes", 4 -> "4_classes", 12 -> "12_classes"
                        const configKey = `${classCount}_classes`;
                        const config = giftCardConfig[configKey as keyof typeof giftCardConfig];
                        if (config) {
                            discountAmount = config.prices[packageCurrency as keyof typeof config.prices] || 0;
                        } else {
                            // Fallback: try to find closest match or use default
                            discountAmount = response.data.giftCard?.currencyValues?.[packageCurrency] || 0;
                        }
                    }
                    
                    // Update packageData
                    if (packageData) {
                        const updatedPackageData = {
                            ...packageData,
                            giftVoucher: giftCardCode.trim(),
                            giftCardDiscount: discountAmount
                        };
                        setPackageData(updatedPackageData);
                    }
                    
                    toast.success("Gift card applied successfully!");
                }
            }
        } catch (error: any) {
            console.error("Error applying gift card:", error);
            setGiftCardError("Please enter a valid gift card code");
            setAppliedGiftCard(null);
            // Clear gift card data from packageData
            if (packageData) {
                const updatedPackageData = {
                    ...packageData,
                    giftVoucher: null,
                    giftCardDiscount: null
                };
                setPackageData(updatedPackageData);
            }
        } finally {
            setGiftCardLoading(false);
        }
    };

    // Function to remove gift card voucher
    const removeGiftCard = () => {
        setGiftCardCode("");
        setGiftCardError("");
        setAppliedGiftCard(null);
        giftCardVerifiedRef.current = null;
        
        // Clear gift card data from packageData
        if (packageData) {
            const updatedPackageData = {
                ...packageData,
                giftVoucher: null,
                giftCardDiscount: null
            };
            setPackageData(updatedPackageData);
        }
        
        toast.success("Gift card removed successfully!");
    };

    // Function to verify gift card voucher status
    const verifyGiftCardVoucher = useCallback(async (voucherCode: string) => {
        if (!voucherCode || !voucherCode.trim()) {
            return false;
        }

        try {
            const apiUrl = `${BLAZE_BASEURL}/api/gift-cards/code/${voucherCode.trim()}/active`;
            const response = await axios.get(apiUrl);
            
            if (response.data && response.data.active === true) {
                // Check if it's a free gift card and if studentId matches
                if (response.data.giftCard?.isFree === true) {
                    const packageStudentId = packageData?.studentId || profileData?.studentId || studentId;
                    const giftCardStudentId = response.data.giftCard?.studentId;
                    
                    if (packageStudentId && giftCardStudentId && +packageStudentId === +giftCardStudentId) {
                        // Free gift card not applicable for the same user
                        setGiftCardCode("");
                        setGiftCardError("Free gift card not applicable for the same user");
                        setAppliedGiftCard(null);
                        if (packageData) {
                            const updatedPackageData = {
                                ...packageData,
                                giftVoucher: null,
                                giftCardDiscount: null
                            };
                            setPackageData(updatedPackageData);
                        }
                        return false;
                    }
                }
                
                // Voucher is active, update packageData with discount
                const packageCurrency = packageData?.courseData?.[0]?.currency || 'INR';
                const giftCardCurrency = response.data.giftCard?.currency || 'INR';
                const classCount = response.data.giftCard?.classCount?.toString() || '4';
                
                // Determine discount amount
                let discountAmount = 0;
                if (giftCardCurrency === packageCurrency) {
                    discountAmount = response.data.giftCard?.currencyValues?.[packageCurrency] || 0;
                } else {
                    const configKey = `${classCount}_classes`;
                    const config = giftCardConfig[configKey as keyof typeof giftCardConfig];
                    if (config) {
                        discountAmount = config.prices[packageCurrency as keyof typeof config.prices] || 0;
                    } else {
                        discountAmount = response.data.giftCard?.currencyValues?.[packageCurrency] || 0;
                    }
                }
                
                // Update packageData
                if (packageData) {
                    const updatedPackageData = {
                        ...packageData,
                        giftVoucher: voucherCode.trim(),
                        giftCardDiscount: discountAmount
                    };
                    setPackageData(updatedPackageData);
                }
                
                setAppliedGiftCard(response.data);
                return true;
            } else {
                // Voucher is not active, clear it
                setGiftCardCode("");
                setGiftCardError("");
                setAppliedGiftCard(null);
                if (packageData) {
                    const updatedPackageData = {
                        ...packageData,
                        giftVoucher: null,
                        giftCardDiscount: null
                    };
                    setPackageData(updatedPackageData);
                }
                return false;
            }
        } catch (error: any) {
            console.error("Error verifying gift card:", error);
            // On error, clear the voucher
            setGiftCardCode("");
            setGiftCardError("");
            setAppliedGiftCard(null);
            if (packageData) {
                const updatedPackageData = {
                    ...packageData,
                    giftVoucher: null,
                    giftCardDiscount: null
                };
                setPackageData(updatedPackageData);
            }
            return false;
        }
    }, [packageData]);

    // Effect to verify gift card voucher when packageData is ready
    useEffect(() => {
        if (selectedPackage?.giftVoucher && packageData && packageData.courseData && packageData.courseData.length > 0) {
            // Only verify if we haven't already verified this voucher code
            if (giftCardVerifiedRef.current !== selectedPackage.giftVoucher) {
                giftCardVerifiedRef.current = selectedPackage.giftVoucher;
                // Verify voucher status
                verifyGiftCardVoucher(selectedPackage.giftVoucher).catch((error) => {
                    console.error("Error in gift card verification:", error);
                    giftCardVerifiedRef.current = null;
                });
            }
        } else if (!selectedPackage?.giftVoucher) {
            // Clear the ref when there's no voucher
            giftCardVerifiedRef.current = null;
        }
    }, [packageData, selectedPackage?.giftVoucher, verifyGiftCardVoucher]);

    const packageCurrency = resolvePackageCurrency(packageData, profileData, paymentConfig);
    const effectiveCourseId = getEffectiveCourseId(selectedCourse, packageData);
    const showComboInstrument = isComboInstrumentEligible(effectiveCourseId, packageCurrency);
    const allowedInstrumentCategory = getDefaultInstrumentCategoryForCourse(effectiveCourseId);
    const isComboInstrumentMode = showComboInstrument && instrument.enabled;
    const demoDiscount = getDemoDiscountRule();

    useEffect(() => {
        if (!showComboInstrument || !instrument.enabled || instrument.category === allowedInstrumentCategory) return;
        const correctedInstrument = enforceInstrumentCategoryForCourse(instrument, effectiveCourseId);
        setInstrument(correctedInstrument);
        syncInstrumentToPackageData(correctedInstrument);
    }, [showComboInstrument, effectiveCourseId, allowedInstrumentCategory, instrument.enabled, instrument.category]);

    useEffect(() => {
        if (!packageData || showComboInstrument || !instrument.enabled) return;
        const clearedInstrument = getDefaultInstrumentForCourse(effectiveCourseId);
        setInstrument(clearedInstrument);
        setPackageData((prev: any) => (prev ? { ...prev, instrumentCombo: null, courseAndInsDiscount: null } : prev));
    }, [showComboInstrument, effectiveCourseId]);

    useEffect(() => {
        if (!packageData?.customPlan || !isComboInstrumentMode) return;
        const needsWalletClear = !packageData.removeWallet || +(packageData.customPlan.walletAmount || 0) > 0;
        if (!isComboInstrumentPackagePresetApplied(packageData.customPlan) || needsWalletClear) {
            applyComboInstrumentPackageSettings(instrument);
        }
    }, [isComboInstrumentMode, packageData?.customPlan?.levelList, packageData?.customPlan?.specialDiscountPercentage, packageData?.removeWallet, packageData?.customPlan?.walletAmount]);

    return (
        <div className="cp">
            <div className="cp-row">
                <Field label="Course" className="cp-row__field">
                    {(ctl) => (
                        <Select
                            {...ctl}
                            disabled={selectedPackage}
                            value={selectedCourse}
                            onChange={handleCourseChange}
                            options={[
                                { value: "", label: "Select a course" },
                                ...courses.map((course: any) => ({ value: String(course.paidCourseId), label: course.name })),
                            ]}
                        />
                    )}
                </Field>
                {selectedCourse && removeManual && (
                    <Field label="Select teacher manually" className="cp-row__field">
                        {(ctl) => (
                            <Select
                                {...ctl}
                                value={dropdownTeacherId}
                                onChange={handleTeacherChange}
                                options={[
                                    { value: "", label: "Please Select teacher" },
                                    ...teachers
                                        .filter(teacher =>
                                            teacher.workingStatus === "ACTIVE" &&
                                            teacher.enableForPaid === "true"
                                        )
                                        .sort((a, b) => {
                                            const priorityA = a.allocationPriority === "1" ? 0 : 1;
                                            const priorityB = b.allocationPriority === "1" ? 0 : 1;
                                            return priorityA - priorityB;
                                        })
                                        .map(teacher => ({
                                            value: String(teacher.teacherId),
                                            label: `${teacher.teacherName ?? ""}${teacher.allocationPriority === "1" ? " - PRIORITY" : ""}`,
                                        })),
                                ]}
                            />
                        )}
                    </Field>
                )}
                {selectedCourse && removeManual && <span className="cp-row__or ds-text-muted">or</span>}
                {selectedCourse && (
                    <Button variant="secondary" onClick={() => setFindTeacherModal(true)}>
                        Find teacher
                    </Button>
                )}
                {selectedCourse && (
                    <Field label="Selected teacher from Find teacher" className="cp-row__field">
                        {(ctl) => (
                            <TextInput
                                {...ctl}
                                value={findTeacherId ? getTeacherNameById(findTeacherId) : ""}
                                disabled
                                readOnly
                            />
                        )}
                    </Field>
                )}
            </div>
            {error && <Callout tone="danger">{error}</Callout>}

            {selectedCourse && (
                <div className="cp-row">
                    {isSplit && !isSplitEdit ? (
                        <Field label="Split into" className="cp-row__field cp-row__field--narrow">
                            {(ctl) => (
                                <Select
                                    {...ctl}
                                    value={splitParts}
                                    onChange={(e) => { setSplitParts(+e.target.value); setSplitPreview(null); }}
                                    options={[
                                        { value: "2", label: "2 payment links" },
                                        { value: "3", label: "3 payment links" },
                                        { value: "4", label: "4 payment links" },
                                    ]}
                                />
                            )}
                        </Field>
                    ) : null}
                    {!isExternal ? (
                        <Field label="Expiry date" className="cp-row__field cp-row__field--narrow">
                            {(ctl) => (
                                <DatePicker
                                    id={ctl.id}
                                    selected={selectedDate}
                                    onChange={(date: Date | null) => {
                                        if (isComboInstrumentMode) return;
                                        if (date) {
                                            const adjustedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12); // Set time to 12 PM to avoid timezone shifts
                                            setSelectedDate(adjustedDate);
                                        }
                                    }}
                                    disabled={isComboInstrumentMode}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Select Date"
                                    className="ds-input"
                                    wrapperClassName="date-picker"
                                />
                            )}
                        </Field>
                    ) : ""}
                    {!isFree ? (
                        <Field label="Sales person name" className="cp-row__field">
                            {(ctl) => (
                                <TextInput
                                    {...ctl}
                                    type="text"
                                    value={salesAgentNameInput}
                                    placeholder="Sales person name"
                                    onChange={(e) => setSalesAgentNameInput(e.target.value)}
                                />
                            )}
                        </Field>
                    ) : null}
                    {packageData && (
                        <div className="cp-wallet">
                            {isSplit ? <p className="cp-wallet__note ds-text-muted">Wallet balance can't be used on split payment links.</p> : null}
                            {!isExternal && !isSplit ? (
                                <label className="form-check-label cp-check">
                                    <Input
                                        checked={!packageData.removeWallet}
                                        onChange={(e) => handleUseWallet(e)}
                                        disabled={isComboInstrumentMode}
                                        type="checkbox"
                                    />
                                    <span className="ds-strong">Use wallet balance</span>
                                </label>
                            ) : ""}
                            {walletDetails && packageData?.courseData?.[0]?.currency && (+walletDetails?.myCash || +walletDetails?.totalArtiumCash) ? (
                                <DescriptionList
                                    items={[
                                        {
                                            label: "Available wallet amount",
                                            value: <>{packageData?.courseData?.[0]?.currency + " "}{(+walletDetails?.myCash + (+walletDetails?.totalArtiumCash * (+walletDetails?.[`${packageData?.courseData?.[0]?.currency}PointConversion`] || 0))).toFixed(2)}</>,
                                        },
                                        {
                                            label: "My Cash",
                                            value: <>{packageData?.courseData?.[0]?.currency + " "}{+walletDetails?.myCash}</>,
                                        },
                                        {
                                            label: "Artium Cash",
                                            value: <>{packageData?.courseData?.[0]?.currency + " "}{(+walletDetails?.totalArtiumCash * (+walletDetails?.[`${packageData?.courseData?.[0]?.currency}PointConversion`] || 0)).toFixed(2)}</>,
                                        },
                                    ]}
                                />
                            ) : ''}
                        </div>
                    )}
                </div>
            )}

            {(!isFree) && packageData ? (
                <div className="cp-plan">
                    <fieldset className="cp-fieldset">
                        <legend className="ds-field__label">Select if Gold or Platinum plan</legend>
                        <div className="cp-choices">
                            <label className="form-check-label cp-check">
                                <Input
                                    type="radio"
                                    name="membership"
                                    value="Silver"
                                    checked={membership === "Silver"}
                                    disabled={isComboInstrumentMode}
                                    onChange={() => handleSelection("Silver")}
                                />
                                Silver
                            </label>
                            <label className="form-check-label cp-check">
                                <Input
                                    type="radio"
                                    name="membership"
                                    value="Gold"
                                    checked={membership === "Gold"}
                                    disabled={isComboInstrumentMode}
                                    onChange={() => handleSelection("Gold")}
                                />
                                Gold
                            </label>
                            <label className="form-check-label cp-check">
                                <Input
                                    type="radio"
                                    name="membership"
                                    value="Platinum"
                                    checked={membership === "Platinum"}
                                    disabled={isComboInstrumentMode}
                                    onChange={() => handleSelection("Platinum")}
                                />
                                Platinum
                            </label>
                            <label className="form-check-label cp-check">
                                <Input
                                    type="radio"
                                    name="membership"
                                    value=""
                                    checked={membership === ""}
                                    disabled={isComboInstrumentMode}
                                    onChange={() => {handleSelection(""); setMembershipAmount(0);}}
                                />
                                Not Applicable
                            </label>
                        </div>
                    </fieldset>

                    {membership ? (
                        <Field label={<>Membership add-on amount ({packageData && packageData.courseData[0] && packageData.courseData[0].currency})</>} className="cp-row__field cp-row__field--narrow">
                            {(ctl) => (
                                <TextInput
                                    {...ctl}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="Enter amount"
                                    value={membershipAmount}
                                    disabled={isComboInstrumentMode}
                                    onChange={(e) => {
                                        if (isComboInstrumentMode) return;
                                        const newValue = e.target.value;
                                        let sanitizedValue = typeof newValue === "string"
                                            ? newValue.replace(/[^0-9]/g, '')  // Remove non-numeric characters
                                            : newValue;
                                        setMembershipAmount(+sanitizedValue);
                                    }}
                                />
                            )}
                        </Field>
                    ) : ""}
                </div>
            ) : ""}

            {!isExternal && packageData?.customPlan && (
                <div className="cp-stack">
                    <Table caption="Package pricing">
                        <thead>
                            <tr>
                                <th>Class Count</th>
                                <th>Class Price</th>
                                <th>Special Discount(%)</th>
                                {!demoDiscount.restricted && !isSplit && (
                                    <>
                                        <th>Additional Discount(%)</th>
                                        <th>Flash Discount(%)</th>
                                    </>
                                )}
                                <th>Price Detail ({packageData.courseData[0].currency})</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="cp-cell-top">
                                    {packageData.customPlan.levelList.map((level: any, index: number) => (
                                        <div key={index} className="cp-level">
                                            <span className="cp-level__name">{level.level}</span>
                                            <TextInput
                                                size="sm"
                                                aria-label={`${level.level} class count`}
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={level.classCount}
                                                disabled={isComboInstrumentMode}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    handleClassCount(level, newValue);
                                                }}
                                                className="cp-num-input"
                                            />
                                        </div>
                                    ))}
                                </td>
                                <td className="cp-cell-top">
                                    {packageData.customPlan.levelList.map((level: any, index: number) => (
                                        <div key={index} className="cp-level">
                                            <span className="cp-level__name cp-level__name--ghost" aria-hidden>{level.level}</span>
                                            <TextInput
                                                size="sm"
                                                aria-label={`${level.level} class price`}
                                                type="text"
                                                disabled={true}
                                                value={level.unit_price}
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                className="cp-num-input"
                                            />
                                        </div>
                                    ))}
                                </td>
                                <td className="cp-cell-top">
                                    <TextInput
                                        size="sm"
                                        aria-label="Special discount percentage"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={packageData.customPlan.specialDiscountPercentage}
                                        disabled={isComboInstrumentMode}
                                        onChange={(e) => handleDiscountChange('specialDiscountPercentage', (e.target.value))}
                                        className="cp-num-input"
                                    />
                                </td>
                                {!demoDiscount.restricted && !isSplit && (
                                    <>
                                        <td className="cp-cell-top">
                                            <TextInput
                                                size="sm"
                                                aria-label="Additional discount percentage"
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={packageData.customPlan.additionalDiscountPercentage}
                                                disabled={isComboInstrumentMode}
                                                onChange={(e) => handleDiscountChange('additionalDiscountPercentage', (e.target.value))}
                                                className="cp-num-input"
                                            />
                                        </td>
                                        <td className="cp-cell-top">
                                            <TextInput
                                                size="sm"
                                                aria-label="Flash discount percentage"
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={packageData.customPlan.flashDiscountPercentage}
                                                disabled={isComboInstrumentMode}
                                                onChange={(e) => handleDiscountChange('flashDiscountPercentage', (e.target.value))}
                                                className="cp-num-input"
                                            />
                                        </td>
                                    </>
                                )}
                                <td className="cp-cell-top">
                                    <div className="cp-breakdown">
                                        {packageData.customPlan.levelList.map((levelData: any, i: any) => (
                                            <div key={i} className="cp-breakdown__row">
                                                <span>{levelData.level} =</span>
                                                <span className="cp-breakdown__value ds-num"><span title='Class Count'>{levelData.classCount}</span> * <span title='Class Price'>{levelData.unit_price}</span></span>
                                            </div>
                                        ))}
                                        <div className="cp-breakdown__row cp-breakdown__row--total">
                                            <span>Total =</span>
                                            <span className="cp-breakdown__value ds-num">{packageData.customPlan.total}</span>
                                        </div>
                                        {packageData.customPlan.specialDiscountPercentage ? (
                                            <div className="cp-breakdown__row">
                                                <span>Special Discount =</span>
                                                <span className="cp-breakdown__value ds-num">-{(((+packageData.customPlan.specialDiscountPercentage) / 100) * packageData.customPlan.total).toFixed(2)} {(+packageData.customPlan.specialDiscountPercentage) > 0 ? `(${(+packageData.customPlan.specialDiscountPercentage).toFixed(2)}%)` : ""}</span>
                                            </div>
                                        ) : ""}
                                        {packageData.customPlan.additionalDiscountPercentage ? (
                                            <div className="cp-breakdown__row">
                                                <span>Additional Discount =</span>
                                                <span className="cp-breakdown__value ds-num">-{(((+packageData.customPlan.additionalDiscountPercentage) / 100) * ((((+packageData.customPlan.specialDiscountPercentage) / 100) * packageData.customPlan.total) || +packageData.customPlan.total)).toFixed(2) }</span>
                                            </div>
                                        ) : ""}
                                        {packageData.customPlan.flashDiscountPercentage && packageData.customPlan.classDateTime && calculateDifference(packageData.customPlan) ? (
                                            <div className="cp-breakdown__row">
                                                <span>Flash Discount =</span>
                                                <span className="cp-breakdown__value ds-num">-{(((+packageData.customPlan.flashDiscountPercentage) / 100) * ((+packageData.customPlan.additionalDiscountPercentage) / 100) * ((+packageData.customPlan.specialDiscountPercentage) / 100) * packageData.customPlan.total).toFixed(2)}</span>
                                            </div>
                                        ) : ""}
                                        {(() => {
                                            if (!showComboInstrument || !instrument.enabled) return null;
                                            const courseTotal = getCourseFinalAmountBeforeInstrument(packageData.customPlan);
                                            const instrumentCalc = getInstrumentCalculations(instrument, courseTotal);
                                            return (
                                                <>
                                                    <div className="cp-breakdown__row">
                                                        <span>{instrumentCalc.name} =</span>
                                                        <span className="cp-breakdown__value ds-num">{instrumentCalc.price}</span>
                                                    </div>
                                                    {instrumentCalc.discountPercentage > 0 ? (
                                                        <div className="cp-breakdown__row">
                                                            <span>Combo Discount =</span>
                                                            <span className="cp-breakdown__value ds-num">-{instrumentCalc.discountAmount.toFixed(2)} ({instrumentCalc.discountPercentage}%)</span>
                                                        </div>
                                                    ) : ""}
                                                </>
                                            );
                                        })()}
                                        {!isComboInstrumentMode && packageData.customPlan.walletAmount ? (
                                            <div className="cp-breakdown__row">
                                                <span>Wallet Amount =</span>
                                                <span className="cp-breakdown__value ds-num">
                                                    -{packageData.customPlan.walletAmount}
                                                    {packageData.customPlan.total ? (
                                                        <> ({((+packageData.customPlan.walletAmount / +packageData.customPlan.total) * 100).toFixed(2)}%)</>
                                                    ) : null}
                                                </span>
                                            </div>
                                        ) : ""}
                                        {!isComboInstrumentMode && packageData.customPlan.walletAmount ? (
                                            <div className="cp-breakdown__row cp-breakdown__row--sub ds-text-muted">
                                                (My Cash : {packageData.customPlan.currency == "INR" ? "₹" : "US$"}{packageData.customPlan.myCash} + Artium Cash : {packageData.customPlan.currency == "INR" ? "₹" : "US$"}{packageData.customPlan.artiumCash})
                                            </div>
                                        ) : ""}
                                        {+membershipAmount ? (
                                            <div className="cp-breakdown__row">
                                                <span>Membership add on =</span>
                                                <span className="cp-breakdown__value ds-num ds-strong">{+membershipAmount}</span>
                                            </div>
                                        ) : ""}
                                        {packageData.giftCardDiscount ? (
                                            <div className="cp-breakdown__row">
                                                <span>Gift Card Discount =</span>
                                                <span className="cp-breakdown__value ds-num">-{packageData.giftCardDiscount}</span>
                                            </div>
                                        ) : ""}
                                        {(() => {
                                            const total = packageData.customPlan.total;
                                            const specialDiscountAmount = (total * (+packageData.customPlan.specialDiscountPercentage || 0) / 100);
                                            const additionalDiscountAmount = (specialDiscountAmount * (+packageData.customPlan.additionalDiscountPercentage || 0) / 100);
                                            const isFlashDiscountApplicable = packageData.customPlan.classDateTime && calculateDifference(packageData.customPlan);
                                            const flashDiscountAmount = isFlashDiscountApplicable ? (additionalDiscountAmount * (+packageData.customPlan.flashDiscountPercentage || 0) / 100) : 0;

                                            let finalAmount;
                                            if (isFlashDiscountApplicable) {
                                                // total - (flashDiscount - (additionalDiscount - (specialDiscount - total)))
                                                finalAmount = total - (flashDiscountAmount + additionalDiscountAmount + specialDiscountAmount);
                                            } else {
                                                // total - (additionalDiscount - (specialDiscount - total))
                                                finalAmount = total - (additionalDiscountAmount + specialDiscountAmount);
                                            }

                                            const giftCardDiscount = packageData.giftCardDiscount || 0;
                                            const walletDeduction = isComboInstrumentMode ? 0 : (packageData.customPlan.walletAmount || 0);
                                            const instrumentCalc = showComboInstrument && instrument.enabled
                                                ? getInstrumentCalculations(instrument, finalAmount)
                                                : getInstrumentCalculations({ enabled: false });

                                            return (
                                                <div className="cp-breakdown__row cp-breakdown__row--total ds-strong">
                                                    <span>Final Amount =</span>
                                                    <span className="cp-breakdown__value ds-num">{((+(membershipAmount || 0) + finalAmount + instrumentCalc.discountedAmount) - walletDeduction - giftCardDiscount).toFixed(2)}</span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <Callout tone="warning">
                        <ul className="cp-notes">
                            {isSplit && (
                                <li>Special discount: <b>max {MAX_SPLIT_DISCOUNT}%</b> on split payments.</li>
                            )}
                            {demoDiscount.restricted && (
                                <li>
                                    Special discount:{" "}
                                    {Number.isFinite(demoDiscount.maxSpecialDiscount)
                                        ? <><b>Max {demoDiscount.maxSpecialDiscount}%</b> for {demoDiscount.classCount} classes</>
                                        : <><b>No discount limit</b> for {demoDiscount.classCount} classes</>}
                                </li>
                            )}
                            {!demoDiscount.restricted && !isSplit && (
                                <>
                                    <li>Additional discount: a flash discount won't be applicable to the user if an additional discount is provided.</li>
                                    <li>Flash discount: applicable if payment is completed within 1 hour after the demo class is marked as completed.</li>
                                </>
                            )}
                        </ul>
                    </Callout>
                </div>
            )}

            {isExternal && packageData?.customPlan && (
                <Table caption="Package pricing">
                    <thead>
                        <tr>
                            <th>Class Count</th>
                            <th>Class Price</th>
                            <th>Discount (%)</th>
                            <th>Price Detail ({packageData.courseData[0].currency})</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="cp-cell-top">
                                {packageData.customPlan.levelList.map((level: any, index: number) => (
                                    <div key={index} className="cp-level">
                                        <span className="cp-level__name">{level.level}</span>
                                        <TextInput
                                            size="sm"
                                            aria-label={`${level.level} class count`}
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={level.classCount}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                handleClassCount(level, newValue);
                                            }}
                                            className="cp-num-input"
                                        />
                                    </div>
                                ))}
                            </td>
                            <td className="cp-cell-top">
                                {packageData.customPlan.levelList.map((level: any, index: number) => (
                                    <div key={index} className="cp-level">
                                        <span className="cp-level__name cp-level__name--ghost" aria-hidden>{level.level}</span>
                                        <TextInput
                                            size="sm"
                                            aria-label={`${level.level} class price`}
                                            type="text"
                                            disabled={true}
                                            value={level.unit_price}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className="cp-num-input"
                                        />
                                    </div>
                                ))}
                            </td>
                            <td className="cp-cell-top">
                                <TextInput
                                    size="sm"
                                    aria-label="Discount percentage"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={packageData.customPlan.discountPercentage}
                                    onChange={(e) => handleDiscountChange('discountPercentage', (e.target.value))}
                                    className="cp-num-input"
                                />
                            </td>
                            <td className="cp-cell-top">
                                <div className="cp-breakdown">
                                    {!isComboInstrumentMode && packageData.customPlan.walletAmount ? (
                                        <div className="cp-breakdown__row">
                                            <span>Wallet Amount =</span>
                                            <span className="cp-breakdown__value ds-num">
                                                {packageData.customPlan.walletAmount}
                                                {packageData.customPlan.total ? ` (${((+packageData.customPlan.walletAmount / +packageData.customPlan.total) * 100).toFixed(2)}%)` : ""}
                                            </span>
                                        </div>
                                    ) : ""}
                                    {membershipAmount ? (
                                        <div className="cp-breakdown__row">
                                            <span>Membership Add ON Amount =</span>
                                            <span className="cp-breakdown__value ds-num">{membershipAmount}</span>
                                        </div>
                                    ) : ""}
                                    {packageData.giftCardDiscount ? (
                                        <div className="cp-breakdown__row">
                                            <span>Gift Card Discount =</span>
                                            <span className="cp-breakdown__value ds-num">-{packageData.giftCardDiscount}</span>
                                        </div>
                                    ) : ""}
                                    {(() => {
                                        if (!showComboInstrument || !instrument.enabled) return null;
                                        const courseTotal = getCourseFinalAmountBeforeInstrument(packageData.customPlan);
                                        const instrumentCalc = getInstrumentCalculations(instrument, courseTotal);
                                        return (
                                            <>
                                                <div className="cp-breakdown__row">
                                                    <span>{instrumentCalc.name} =</span>
                                                    <span className="cp-breakdown__value ds-num">{instrumentCalc.price}</span>
                                                </div>
                                                {instrumentCalc.discountPercentage > 0 ? (
                                                    <div className="cp-breakdown__row">
                                                        <span>Combo Discount =</span>
                                                        <span className="cp-breakdown__value ds-num">-{instrumentCalc.discountAmount.toFixed(2)} ({instrumentCalc.discountPercentage}%)</span>
                                                    </div>
                                                ) : null}
                                            </>
                                        );
                                    })()}
                                    <div className="cp-breakdown__row cp-breakdown__row--total ds-strong">
                                        <span>Total =</span>
                                        <span className="cp-breakdown__value ds-num">{(packageData.customPlan.discountedTotal + (+membershipAmount || 0) - (packageData.giftCardDiscount || 0) + (showComboInstrument && instrument.enabled ? getInstrumentCalculations(instrument, getCourseFinalAmountBeforeInstrument(packageData.customPlan)).discountedAmount : 0)).toFixed(2)}</span>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            )}

            {/* Gift Card Code Input - Below Table (not offered on a split part) */}
            {!isExternal && !isSplit && packageData?.customPlan && (
                <Field
                    label="Gift card code"
                    className="cp-giftcard"
                    error={giftCardError || undefined}
                >
                    {(ctl) => (
                        <div className="cp-inline">
                            <TextInput
                                {...ctl}
                                type="text"
                                placeholder="Enter gift card code"
                                value={packageData?.giftVoucher || giftCardCode}
                                onChange={(e) => {
                                    if (isComboInstrumentMode) return;
                                    setGiftCardCode(e.target.value);
                                    setGiftCardError("");
                                }}
                                onKeyPress={(e) => {
                                    if (isComboInstrumentMode) return;
                                    if (e.key === 'Enter' && !packageData?.giftVoucher) {
                                        applyGiftCardCode();
                                    }
                                }}
                                className="cp-inline__grow"
                                disabled={isComboInstrumentMode || !!packageData?.giftVoucher}
                            />
                            {packageData?.giftVoucher ? (
                                <Button variant="ghost" className="cp-remove" onClick={removeGiftCard} disabled={isComboInstrumentMode}>
                                    Remove
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    onClick={applyGiftCardCode}
                                    loading={giftCardLoading}
                                    disabled={isComboInstrumentMode}
                                >
                                    Apply
                                </Button>
                            )}
                        </div>
                    )}
                </Field>
            )}

            {/* Gift Card Code Input - Below External Payment Table */}
            {isExternal && packageData?.customPlan && (
                <Field
                    label="Gift card code"
                    className="cp-giftcard"
                    error={giftCardError || undefined}
                >
                    {(ctl) => (
                        <div className="cp-inline">
                            <TextInput
                                {...ctl}
                                type="text"
                                placeholder="Enter gift card code"
                                value={packageData?.giftVoucher || giftCardCode}
                                onChange={(e) => {
                                    setGiftCardCode(e.target.value);
                                    setGiftCardError("");
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !packageData?.giftVoucher) {
                                        applyGiftCardCode();
                                    }
                                }}
                                className="cp-inline__grow"
                                disabled={!!packageData?.giftVoucher}
                            />
                            {packageData?.giftVoucher ? (
                                <Button variant="ghost" className="cp-remove" onClick={removeGiftCard}>
                                    Remove
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    onClick={applyGiftCardCode}
                                    loading={giftCardLoading}
                                >
                                    Apply
                                </Button>
                            )}
                        </div>
                    )}
                </Field>
            )}

            {/* Package Options Section (EMI / gift card do not apply to a split part) */}
            {packageData && !isFree && !isExternal && !isSplit && (
                <section className="cp-options" aria-label="Package options">
                    <label className="form-check-label cp-check">
                        <Input
                            type="checkbox"
                            checked={enableEMI}
                            disabled={isComboInstrumentMode}
                            onChange={(e) => handleEmiToggle(e.target.checked)}
                        />
                        <span>Enable EMI Option for this package</span>
                    </label>

                    <label className="form-check-label cp-check">
                        <Input
                            type="checkbox"
                            checked={addGiftCard}
                            disabled={isComboInstrumentMode}
                            onChange={(e) => handleGiftCardCheckboxChange(e.target.checked)}
                        />
                        <span>Add Gift Card</span>
                    </label>

                    {addGiftCard && (
                        <fieldset className="cp-options__sub cp-fieldset">
                            <legend className="ds-field__label">Select gift card option</legend>
                            <div className="cp-choices cp-choices--column">
                                {Object.keys(giftCardConfig).map((optionKey) => (
                                    <label key={optionKey} className="form-check-label cp-check">
                                        <Input
                                            type="radio"
                                            name="giftCardOption"
                                            value={optionKey}
                                            checked={selectedGiftCardOption === optionKey}
                                            onChange={(e) => handleGiftCardOptionChange(e.target.value)}
                                        />
                                        <span>{getGiftCardOptionText(optionKey)}</span>
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                    )}

                    {emailValidationError && (
                        <Callout tone="danger">{emailValidationError}</Callout>
                    )}

                    {showComboInstrument && (
                        <label className="form-check-label cp-check">
                            <Input
                                type="checkbox"
                                checked={instrument.enabled}
                                disabled={enableEMI || addGiftCard}
                                onChange={(e) => handleComboInstrumentToggle(e.target.checked)}
                            />
                            <span>Add Instrument Combo</span>
                        </label>
                    )}

                    {showComboInstrument && instrument.enabled && (
                        <div className="cp-options__sub cp-instrument">
                            <div className="cp-stack">
                                <DescriptionList
                                    items={[{ label: "Instrument type", value: getInstrumentCategory(allowedInstrumentCategory).name }]}
                                />
                                <fieldset className="cp-fieldset">
                                    <legend className="ds-field__label">Model</legend>
                                    <div className="cp-choices cp-choices--column">
                                        {Object.entries(getInstrumentCategory(allowedInstrumentCategory).models).map(([modelKey, modelDetails]) => (
                                            <label key={modelKey} className="form-check-label cp-check">
                                                <Input
                                                    type="radio"
                                                    name="instrumentModel"
                                                    value={modelKey}
                                                    checked={instrument.model === modelKey}
                                                    onChange={() => handleInstrumentModelChange(modelKey)}
                                                />
                                                <span>{getInstrumentModelLabel(modelDetails, instrument.category)}</span>
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>
                            </div>
                            <DescriptionList
                                items={[
                                    { label: "Instrument price (MRP)", value: instrument.price },
                                    { label: "Combo price", value: instrument.discountedPrice },
                                    {
                                        label: "Discount",
                                        value: <>-{getInstrumentCalculations(instrument, getCourseFinalAmountBeforeInstrument(packageData?.customPlan)).discountAmount} ({getInstrumentCalculations(instrument, getCourseFinalAmountBeforeInstrument(packageData?.customPlan)).discountPercentage}%)</>,
                                    },
                                ]}
                            />
                        </div>
                    )}
                </section>
            )}

            {isFree && packageData ? (
                <FormGrid min={320}>
                    <Field label="Reason for free class" required>
                        {(ctl) => (
                            <Select
                                {...ctl}
                                value={freeClassReasonValue}
                                name="paymentModeValue"
                                onChange={(e) => {
                                    setFreeClassReasonValue(e.target.value);
                                    if (e.target.value != "Others") {
                                        setFreeClassReason(e.target.value);
                                    } else {
                                        setFreeClassReason("");
                                    }
                                }}
                                options={[
                                    { value: "", label: "Please select reason for free class" },
                                    ...[
                                        "Compensatory classes (Due to Academic)",
                                        "Compensatory classes (Due to Teacher Escalation)",
                                        "Compensatory classes (Due to Technical Issues)",
                                        "Compensatory classes (Due to Refund retention)",
                                        "Compensatory classes (Class deducted due to cancellation)",
                                        "Compensatory classes (no-show policy)",
                                        "The learner opted for 30mins classes.",
                                        "Expiry extension",
                                        "Operational reasons",
                                        "Others"
                                    ].map((v) => ({ value: v, label: v })),
                                ]}
                            />
                        )}
                    </Field>
                    {freeClassReasonValue == "Others" ? (
                        <Field label="Other reason" required>
                            {(ctl) => (
                                <TextInput {...ctl} type='text' value={freeClassReason} onChange={(e) => setFreeClassReason(e.target.value)} placeholder='Please enter the reason for free class' />
                            )}
                        </Field>
                    ) : ""}
                </FormGrid>
            ) : ""}

            {(isExternal && !isFree) && packageData ? (
                <FormGrid min={240}>
                    <Field label="Payment reference number">
                        {(ctl) => (
                            <TextInput
                                {...ctl}
                                type="text"
                                name="refNumber"
                                value={refNumber}
                                onChange={(e) => setRefNumber(e.target.value)}
                            />
                        )}
                    </Field>
                    <Field label="Payment mode" required>
                        {(ctl) => (
                            <Select
                                {...ctl}
                                value={paymentModeValue}
                                name="paymentModeValue"
                                onChange={(e) => {
                                    setPaymentModeValue(e.target.value);
                                    if (e.target.value != "Others") {
                                        setPaymentMode(e.target.value);
                                    } else {
                                        setPaymentMode("");
                                    }
                                }}
                                options={["Stripe", "PayPal", "Bank transaction", "UPI", "Cash", "xPay", "Others"].map((v) => ({ value: v, label: v }))}
                            />
                        )}
                    </Field>
                    {paymentModeValue == "Others" ? (
                        <Field label="Other payment mode" required>
                            {(ctl) => (
                                <TextInput {...ctl} type='text' value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} placeholder='Please enter other payment mode' />
                            )}
                        </Field>
                    ) : ""}
                </FormGrid>
            ) : ""}

            <Actions align="end">
                {isExternal ? (
                    <Button
                        variant="primary"
                        onClick={() => {
                            // if (!(findTeacherId || dropdownTeacherId)) {
                            //     toast.error("Please select teacher");
                            //     return;
                            // }
                            if(isFree && !freeClassReason.trim()){
                                toast.error("Please enter the reason for free class");
                                return;
                            }
                            if (!isFree) {
                                if (!paymentMode) {
                                    toast.error("Please enter payment mode");
                                    return;
                                }
                            }
                            if(!isFree && membership == null){
                                toast.error("Please select plan details!");
                                return;
                            }
                            debouncedCallExternalPayment();
                        }} // Update with actual function if needed
                        loading={loading}
                    >
                        Create Class
                    </Button>
                ) : isSplitEdit ? (
                    <Button variant="primary" onClick={updateSplitPart} loading={loading}>
                        Update payment link
                    </Button>
                ) : isSplit ? (
                    <>
                        {selectedCourse && packageData?.customPlan ? (
                            <Button
                                variant="secondary"
                                onClick={previewSplit}
                                loading={previewLoading}
                                disabled={loading}
                            >
                                Preview split
                            </Button>
                        ) : null}
                        <Button variant="primary" onClick={createSplitFromPlan} loading={loading}>
                            Create split payment
                        </Button>
                    </>
                ) : (
                    <Button variant="primary" onClick={debouncedSavePackageData} loading={loading}>
                        Save Package
                    </Button>
                )}
            </Actions>

            {isSplit && splitPreview && (
                <div className="cp-stack">
                    <p className="cp-split-summary ds-text-muted">
                        Total {formatSplitMoney(splitPreview.totalBefore, splitCurrency)} · Discount{" "}
                        {formatSplitMoney(splitPreview.discount, splitCurrency)} · Final{" "}
                        <b>{formatSplitMoney(splitPreview.finalTotal, splitCurrency)}</b>{" "}
                        ({String(splitCurrency || "INR").toUpperCase()})
                    </p>
                    <Table caption="Split payment preview">
                        <thead>
                            <tr>
                                <th>Part</th>
                                <th>Levels</th>
                                <th className="ds-num">Classes</th>
                                <th className="ds-num">Price</th>
                                <th className="ds-num">Off</th>
                                <th className="ds-num">Off %</th>
                                <th className="ds-num">Payable</th>
                            </tr>
                        </thead>
                        <tbody>
                            {splitPreview.parts.map((part: any) => {
                                const classes = part.assigned.reduce((sum: number, a: any) => sum + (+a.qty || 0), 0);
                                const gross = part.assigned.reduce((sum: number, a: any) => sum + (+a.qty || 0) * (+a.unitPrice || 0), 0);
                                const off = Math.max(gross - part.amount, 0);
                                // the effective rate stored on this part as specialDiscountPercentage
                                const offPct = gross > 0 ? (off / gross) * 100 : 0;
                                return (
                                    <tr key={part.part}>
                                        <td className="cp-nowrap">Part {part.part} of {splitPreview.parts.length}</td>
                                        <td>{part.assigned.map((a: any) => `${a.name} ${a.qty}`).join(", ")}</td>
                                        <td className="ds-num">{classes}</td>
                                        <td className="ds-num">{formatSplitMoney(gross, splitCurrency)}</td>
                                        <td className="ds-num">{formatSplitMoney(off, splitCurrency)}</td>
                                        <td className="ds-num">{offPct.toFixed(2)}%</td>
                                        <td className="ds-num ds-strong">{formatSplitMoney(part.amount, splitCurrency)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}

            <Dialog isOpen={findTeacherModal} onClose={() => setFindTeacherModal(false)} title="Find teacher" size="xl" className="find-teacher-dialog">
                <FindTeacher
                    studentId={studentId}
                    profileData={profileData}
                    preselectedCourseId={selectedCourse}
                    onTeacherSelect={(teacher) => {
                        setFindTeacherId(teacher.teacherId);
                        // If teacherName is missing, construct it from firstName and lastName
                        let teacherWithName = teacher;
                        if (!teacher.teacherName) {
                            teacherWithName = {
                                ...teacher,
                                teacherName: (teacher.firstName || "") + (teacher.lastName ? (" " + teacher.lastName) : "")
                            };
                        }
                        if (!teachers.some((t) => t.teacherId === teacher.teacherId)) {
                            setTeachers((prev) => [...prev, teacherWithName]);
                        }
                        setFindTeacherModal(false);
                    }}
                    disableCourseSelect={true}
                />
            </Dialog>
        </div>
    );
};

export default CustomPayment;